import {NavigateFunction} from 'react-router-dom';
import {getUserTokenOrRedirect} from '../../shared/services/auth.service';

/** SQ-712: one company's AI pricing switch, as the admin tab lists it. */
export interface AdminCompanyAiPricingDto {
  companyId: number;
  companyName: string;
  companyCreatedAt: string;
  memberCount: number;
  aiPricingEnabled: boolean;
  /** When the switch last moved (UTC, no zone on the wire); null when it never has. */
  changedAt: string | null;
  changedByName: string | null;
}

async function aiPricingRequest<T>(navigate: NavigateFunction, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/ai-pricing${path}`,
    {...init, headers: {...init.headers, Authorization: `Bearer ${token}`}});
  if (!response.ok) {
    const body = await response.json().catch(() => null) as {message?: string; error?: string} | null;
    throw new Error(body?.message || body?.error || `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

export const getAiPricing = (navigate: NavigateFunction) =>
  aiPricingRequest<AdminCompanyAiPricingDto[]>(navigate, '');

export const setAiPricing = (navigate: NavigateFunction, companyId: number, enabled: boolean) =>
  aiPricingRequest<AdminCompanyAiPricingDto>(navigate, `/companies/${companyId}`, {
    method: 'PUT',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({enabled}),
  });

/** "Turned on Oct 26, 2026 by Caleb Cook"; "Never turned on" when the switch has not moved since it was added. */
export const changedLine = (company: AdminCompanyAiPricingDto) => {
  if (!company.changedAt) return 'Never turned on';
  const at = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(company.changedAt) ? company.changedAt : `${company.changedAt}Z`);
  const day = at.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  const by = company.changedByName ? ` by ${company.changedByName}` : '';
  return `Turned ${company.aiPricingEnabled ? 'on' : 'off'} ${day}${by}`;
};
